import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../AppIcon';
import Button from './Button';
import { useContent } from '../../hooks/useContent';
import { SocialLink } from '../../pages/home-landing/types';
import { defaultSocialLinks } from '../../pages/home-landing/constants/defaults';

interface HeaderProps {
  className?: string;
  socialLinks?: SocialLink[];
}

const Header = ({ className = '', socialLinks: propSocialLinks }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Fetch social links if not provided via props
  const { content: fetchedSocialLinks } = useContent<SocialLink[]>('social_links', defaultSocialLinks);
  // OVERRIDE: Use defaults for the top bar to ensure exact match with reference design
  const socialLinks = defaultSocialLinks;

  const navigationItems = [
    { label: 'Home', path: '/home-landing', icon: 'Home' },
    { label: 'About', path: '/about-profile', icon: 'User' },
    { label: 'Projects', path: '/projects-showcase', icon: 'FolderOpen' },
    { label: 'Contact', path: '/contact-form', icon: 'Mail' },
  ];

  const isActiveRoute = (path: string) => {
    if (path === '/home-landing' && location.pathname === '/') return true;
    return location.pathname === path;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);


  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${className}`}>
        {/* Top Professional Bar */}
        <div className="bg-white py-2 px-6 sm:px-12 border-b border-[#0C2C39]/5">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <span className="text-[13px] font-bold text-[#0C2C39] tracking-tight">
              Have a project idea? Feel free to reach out
            </span>
            <div className="flex items-center space-x-6">
              {socialLinks.map((link) => (
                <a
                  key={link.id}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#0C2C39]/80 hover:text-[#0C2C39] transition-colors"
                >
                  <Icon name={link.icon as any} size={18} strokeWidth={2.5} />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Main Navigation Bar */}
        <div className="bg-[#FFF9EB] py-4 px-6 sm:px-12 border-b border-[#0C2C39]/5">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            {/* Logo */}
            <Link
              to="/home-landing"
              className="group flex items-center space-x-3 transition-all duration-300"
              onClick={closeMobileMenu}
            >
              <div className="text-3xl font-black text-[#0C2C39] tracking-tighter">
                YK
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-10">
              {[
                { label: 'Home', path: '/home-landing' },
                { label: 'Portfolio', path: '/projects-showcase' },
                { label: 'About', path: '/about-profile' },
                { label: 'My services', path: '#services' },
                { label: 'Blog', path: '#blog' }
              ].map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm font-bold tracking-tight transition-all duration-300 ${isActiveRoute(item.path)
                    ? 'text-[#0C2C39] border-b-2 border-[#0C2C39] pb-0.5'
                    : 'text-[#0C2C39]/60 hover:text-[#0C2C39]'
                    }`}
                >
                  {item.label}
                </Link>
              ))}

              <Link to="/contact-form">
                <Button
                  className="px-8 py-3 bg-transparent border-2 border-[#0C2C39] text-[#0C2C39] font-black  tracking-widest text-[10px] rounded-[11px] hover:bg-[#0C2C39] hover:text-white transition-all duration-300 ml-4"
                >
                  Contact me
                </Button>
              </Link>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-[#0C2C39]"
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              <Icon
                name={isMobileMenuOpen ? "X" : "Menu"}
                size={24}
              />
            </button>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;